import {
  useEffect,
  useState
} from "react";

import {
  useNavigate,
  useParams
} from "react-router-dom";

import apiClient from "../../shared/API/apiClient";

import "../../styles/pages/record_entry_form.css";
import { useReportingYear } from "../../context/ReportingYearContext";
// =========================================
// HELPERS
// =========================================

const formatNumber = (
  value,
  decimals = 2
) => {

  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "—";
  }

  return Number(value).toLocaleString(
    "en-IN",
    {
      minimumFractionDigits:
        decimals,
      maximumFractionDigits:
        decimals
    }
  );
};

const formatDate = (
  dateString
) => {

  if (!dateString) {
    return "—";
  }

  return new Date(
    dateString
  ).toLocaleDateString(
    "en-GB",
    {
      day: "2-digit",
      month: "short",
      year: "numeric"
    }
  );
};

// =========================================
// PAGE
// =========================================

const ViewActivityPage = () => {

const {
  reportingYearId
} = useReportingYear();
const [documents, setDocuments] =
  useState([]);
const [
  activityTypes,
  setActivityTypes
] = useState([]);

  const fetchActivityTypes =
  async () => {

    try {

      const response =
        await apiClient.get(
          "/api/activitytype",
          {
            params: {
              reporting_year_id:
                reportingYearId
            }
          }
        );

      setActivityTypes(
        response.data?.data?.data ||
        response.data?.data ||
        []
      );

    } catch (err) {

      console.error(
        "ACTIVITY TYPES ERROR",
        err
      );
    }
  };

  // =========================================
  // NAVIGATION
  // =========================================

  const navigate =
    useNavigate();

  const { id } =
    useParams();

  // =========================================
  // STATES
  // =========================================

  const [loading, setLoading] =
    useState(false);

  const [activity, setActivity] =
    useState(null);

  const [activityConfig,
    setActivityConfig
  ] = useState(null);

  const [entries, setEntries] =
    useState([]);

  // =========================================
  // LOAD DATA
  // =========================================

  useEffect(() => {

  if (
    !id ||
    !reportingYearId
  ) {
    return;
  }

  fetchActivity();
  fetchActivityTypes();

}, [id, reportingYearId]);
  // =========================================
  // FETCH ACTIVITY
  // =========================================

  const fetchActivity =
    async () => {

      try {

        setLoading(true);

        // =====================================
        // FETCH ACTIVITY
        // =====================================

        const response =
          await apiClient.get(
            "/api/activity-data/by-id",
            {
              params: {
                activity_data_id:
                  id
              }
            }
          );

        const record =
          response.data?.record;

        if (!record) {
          return;
        }

        setActivity(record);
        setDocuments(
  response.data?.documents || []
);

        // =====================================
        // FETCH CONFIG
        // =====================================

        const configResponse =
          await apiClient.get(
            `/api/get-activity-data?activitytype_id=${record.activity_type_id}`
          );

        const config =
          configResponse.data?.data?.[0] || null;

        setActivityConfig(config);

        // =====================================
        // PARSE ENTRIES
        // =====================================

        const parsedEntries =
          record.data_json
            ? JSON.parse(
                record.data_json
              )
            : [];

        setEntries(parsedEntries);

      } catch (err) {

        console.error(
          "VIEW ACTIVITY ERROR",
          err
        );

      } finally {

        setLoading(false);
      }
    };

  // =========================================
  // CONFIG PARSING
  // =========================================

  const sourceDetails =
    activityConfig
      ?.source_field_details
      ? JSON.parse(
          activityConfig.source_field_details
        )
      : null;

  const disposalDetails =
    activityConfig
      ?.disposal_field_details
      ? JSON.parse(
          activityConfig.disposal_field_details
        )
      : null;

  // =========================================
  // DYNAMIC COLUMN TITLE
  // =========================================

  let firstColumnLabel =
    "Source";

  if (
    sourceDetails?.field_label
  ) {

    firstColumnLabel =
      sourceDetails.field_label;
  }

  if (
    disposalDetails?.field_label
  ) {

    firstColumnLabel =
      disposalDetails.field_label;
  }

  if (
    Number(
      activityConfig?.requires_parameters
    ) === 1
  ) {

    firstColumnLabel =
      "Parameter";
  }

  // =========================================
  // SHOW EMISSION COLUMNS
  // =========================================

  const showEmissionColumns =
    entries.some(
      (item) => {

        return (
          item.emission?.is_applicable === true ||
          Number(
            item.emission_factor || 0
          ) > 0
        );
      }
    );

  // =========================================
  // LOADING
  // =========================================

  if (loading) {

    return (

      <div className="dash-page">

        Loading...

      </div>

    );
  }

  // =========================================
  // NO DATA
  // =========================================

  if (!activity) {

    return (

      <div className="dash-page">

        No activity found.

      </div>

    );
  }

  // =========================================
  // RENDER
  // =========================================

  return (

    <div className="dash-page">

      {/* HEADER */}

      <div className="dash-page-header">

        <div>

          <div className="dash-page-subtitle">
            ENVIRONMENT & CARBON
          </div>

          <div className="va-heading__title">
            View Entry
          </div>

          <div className="va-heading__desc">
            Read-only view of this activity record.
          </div>

<div class="va-heading__meta"><span>Submitted by <strong>{
                activity.submitted_by
              }</strong></span><span> · Submitted on <strong>{formatDate(
                activity.created_at
              )}</strong></span><span class="va-heading__readonly"><i class="fa-solid fa-eye" aria-hidden="true"></i> Read-only review</span></div>
        </div>

        <button
          className="org-btn-save"
          onClick={() =>
            navigate(
              "/dashboard/energy-consumption"
            )
          }
        >
          ← Back to Records
        </button>

      </div>

      {/* ENTRY DETAILS */}

      <div className="le-card">

        <div className="ecf-section-title">

          📋 Entry Details

        </div>

        <div className="ecf-grid-3">

          {/* REPORTING YEAR */}

          <div className="org-form-field">

            <label className="org-field-label">
              REPORTING YEAR
            </label>

            <div className="org-field-input read-only-field">

              2026-27

            </div>

          </div>

          {/* LOCATION */}

          <div className="org-form-field">

            <label className="org-field-label">
              LOCATION
            </label>

            <div className="org-field-input read-only-field">

              {activity.site_name}

            </div>

          </div>

          {/* ENTITY */}

          <div className="org-form-field">

            <label className="org-field-label">
              LEGAL ENTITY
            </label>

            <div className="org-field-input read-only-field">

              {activity.entity_name}

            </div>

          </div>

          {/* DEPARTMENT */}

          <div className="org-form-field">

            <label className="org-field-label">
              DEPARTMENT
            </label>

            <div className="org-field-input read-only-field">

              {activity.department_name || "—"}

            </div>

          </div>

          {/* DATE */}

          <div className="org-form-field">

            <label className="org-field-label">
              DATE
            </label>

            <div className="org-field-input read-only-field">

              {formatDate(
                activity.entry_date
              )}

            </div>

          </div>

          {/* ACTIVITY TYPE */}

          <div className="org-form-field">

            <label className="org-field-label">
              ACTIVITY TYPE
            </label>

            <div className="org-field-input read-only-field">

              {
  activityTypes.find(
    (item) =>
      String(item.id) ===
      String(activity.activity_type_id)
  )?.name || "—"
}

            </div>

          </div>

        </div>

      </div>

      {/* DYNAMIC SECTION */}

      <div
        className="le-card"
        style={{
          marginTop: 20
        }}
      >

        <div className="ecf-section-title">

          ⚡ {
  activityTypes.find(
    (item) =>
      String(item.id) ===
      String(activity.activity_type_id)
  )?.name || "Activity"
} Details

        </div>

        {/* =====================================
    MONITORING VIEW
===================================== */}

{entries?.[0]?.field_type === "parameter" ? (

  <div className="ecf-monitoring-view-wrapper">

    {entries.map((entry, index) => (

      <div
        key={index}
        className="ecf-monitoring-view-card"
      >

        {/* TOP INFO */}

        <div className="ecf-monitoring-view-top">

          <div className="ecf-monitoring-view-item">

            <div className="ecf-monitoring-view-label">
              Sample Point
            </div>

            <div className="ecf-monitoring-view-value">
              {entry.option_label}
            </div>

          </div>

          <div className="ecf-monitoring-view-item">

            <div className="ecf-monitoring-view-label">
              Quantity
            </div>

            <div className="ecf-monitoring-view-value">

              {formatNumber(
                entry.value,
                0
              )}

              {" "}

              {entry.unit}

            </div>

          </div>

        </div>

        {/* PARAMETERS */}

        <div className="ecf-monitoring-params-table">

          <table className="ecf-table">

            <thead>

              <tr>

                <th>
                  Parameter
                </th>

                <th>
                  Value
                </th>

                <th>
                  Unit
                </th>

                <th>
                  Range
                </th>

              </tr>

            </thead>

            <tbody>

              {entry.parameters?.map(
                (param, pIndex) => (

                  <tr key={pIndex}>

                    <td>

                      {param.parameter_name}

                    </td>

                    <td>

                      <strong>

                        {param.value}

                      </strong>

                    </td>

                    <td>

                      {param.unit}

                    </td>

                    <td>

                      {param.min_limit || 0}

                      {" - "}

                      {param.max_limit || "∞"}

                    </td>

                  </tr>

                )
              )}

            </tbody>

          </table>

        </div>

      </div>

    ))}

  </div>

) : (

  /* =====================================
      EXISTING NORMAL TABLE
  ===================================== */

  <div className="ecf-table-wrapper">

    <table className="ecf-table">

      <thead>

        <tr>

          <th>
            {firstColumnLabel}
          </th>

          <th>
            Value
          </th>

          {showEmissionColumns && (
            <>
              <th>
                Emission Factor
              </th>

              <th>
                Estimated Emission
              </th>
            </>
          )}

        </tr>

      </thead>

      <tbody>

        {entries.map(
          (item, index) => {

            const value =
              item.value ||
              item.entered_value;

            const factor =
              item.emission?.factor ||
              item.emission_factor;

            const factorUnit =
              item.emission?.factor_unit ||
              item.emission_factor_unit;

            const emissionValue =
              item.emission?.value ||
              item.estimated_emission;

            return (

              <tr key={index}>

                <td>

                  {
                    item.option_label ||
                    item.selected_option?.option_value
                  }

                </td>

                <td>

                  <strong>

                    {formatNumber(
                      value,
                      0
                    )}

                  </strong>

                  {" "}

                  {item.unit}

                </td>

                {showEmissionColumns && (

                  <>

                    <td>

                      {factor || 0}

                      {" "}

                      {factorUnit || ""}

                    </td>

                    <td>
{emissionValue ? (
  <>
    {formatNumber(emissionValue, 2)} tCO₂e
  </>
) : (
  "-"
)}
                    </td>

                  </>

                )}

              </tr>

            );
          }
        )}

      </tbody>

    </table>

  </div>

)}

        {/* NOTES & EVIDENCE */}

        <div
          className="ecf-notes-evidence-grid"
          style={{
            marginTop: 30
          }}
        >

          {/* NOTES */}

          <div className="org-form-field">

            <label className="org-field-label">
              NOTES
            </label>

            <div
              className="org-field-input read-only-field"
              style={{
                minHeight: 140,
                paddingTop: 18
              }}
            >

              {
                activity.notes ||
                "No notes added."
              }

            </div>

          </div>

          {/* EVIDENCE */}

          <div className="org-form-field">

            <label className="org-field-label">
              EVIDENCE
            </label>

            {
  documents.length > 0 ? (

    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        width: "100%"
      }}
    >

      {documents.map((doc) => (

        <a
          key={doc.id}
          href={doc.file_url}
          target="_blank"
          rel="noreferrer"
          className="ecf-document-link"
        >

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10
            }}
          >

            <span>
              📎
            </span>

            <div>

              <div
                style={{
                  fontWeight: 600
                }}
              >
                {doc.file_name}
              </div>

              <div
                style={{
                  fontSize: 12,
                  opacity: 0.7,
                  marginTop: 2
                }}
              >

                Uploaded on{" "}

                {
                  new Date(
                    doc.uploaded_at
                  ).toLocaleDateString(
                    "en-GB",
                    {
                      day: "2-digit",
                      month: "short",
                      year: "numeric"
                    }
                  )
                }

              </div>

            </div>

          </div>

        </a>

      ))}

    </div>

  ) : (

    <div>

     No evidence uploaded

    </div>

  )
}

          </div>

        </div>

      </div>

    </div>
  );
};

export default ViewActivityPage;